import { Controller } from "@hotwired/stimulus"

/**
 * Sidebar Nav Controller — Accordion for collapsible app bins.
 *
 * - One app open at a time (accordion)
 * - Persists state in localStorage
 * - Auto-opens the current app based on `currentApp` value
 * - Uses requestAnimationFrame for smooth, glitch-free transitions
 * - Turbo-aware: skips restore if already initialized
 */
export default class extends Controller {
  static targets = ["section", "panel"]
  static values = { currentApp: String }

  connect() {
    this.storageKey = "90mod-sidebar-open"
    this.animating = new Set()
    
    // Skip restore if this is a Turbo navigation and we're already initialized
    // (sidebar persists across navigations, no need to re-animate)
    if (this.element.dataset.initialized === "true") {
      return
    }
    
    this.restoreState()
    this.element.dataset.initialized = "true"
  }

  toggle(event) {
    event.preventDefault()
    const key = event.params.key
    if (!key || this.animating.has(key)) return

    const wasOpen = this.isOpen(key)

    // Close all open panels
    this.sectionTargets.forEach(section => {
      const sectionKey = section.dataset.sectionKey
      if (this.isOpen(sectionKey) && sectionKey !== key) {
        this.closePanel(sectionKey)
      }
    })

    if (wasOpen) {
      this.closePanel(key)
    } else {
      this.openPanel(key)
    }

    this.saveState()
  }

  openPanel(key) {
    const panel = this.findPanel(key)
    const section = this.findSection(key)
    if (!panel || !section) return

    this.animating.add(key)
    section.classList.add("sidebar-app--open")

    // Measure the content height
    panel.style.maxHeight = "none"
    const height = panel.scrollHeight
    panel.style.maxHeight = "0px"

    // Force layout, then animate
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.opacity = "1"
        panel.style.maxHeight = height + "px"
      })
    })

    const cleanup = () => {
      panel.style.maxHeight = "none" // Allow dynamic content growth
      this.animating.delete(key)
      panel.removeEventListener("transitionend", cleanup)
    }
    panel.addEventListener("transitionend", cleanup, { once: true })

    // Safety timeout in case transitionend doesn't fire
    setTimeout(() => {
      this.animating.delete(key)
      if (section.classList.contains("sidebar-app--open")) {
        panel.style.maxHeight = "none"
      }
    }, 300)
  }

  closePanel(key) {
    const panel = this.findPanel(key)
    const section = this.findSection(key)
    if (!panel || !section) return

    this.animating.add(key)
    section.classList.remove("sidebar-app--open")

    // Set explicit height first so CSS transition has a start value
    panel.style.maxHeight = panel.scrollHeight + "px"

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.opacity = "0"
        panel.style.maxHeight = "0px"
      })
    })

    const cleanup = () => {
      this.animating.delete(key)
      panel.removeEventListener("transitionend", cleanup)
    }
    panel.addEventListener("transitionend", cleanup, { once: true })

    setTimeout(() => this.animating.delete(key), 300)
  }

  isOpen(key) {
    return this.findSection(key)?.classList.contains("sidebar-app--open") ?? false
  }

  findPanel(key) {
    return this.panelTargets.find(p => p.dataset.panelKey === key)
  }

  findSection(key) {
    return this.sectionTargets.find(s => s.dataset.sectionKey === key)
  }

  saveState() {
    const openKey = this.sectionTargets
      .find(s => s.classList.contains("sidebar-app--open"))
      ?.dataset.sectionKey || null
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(openKey))
    } catch (_) { /* localStorage unavailable */ }
  }

  restoreState() {
    // Initialize all panels collapsed (no transition during restore)
    this.panelTargets.forEach(panel => {
      // Temporarily disable transitions to prevent flicker
      panel.style.transition = "none"
      panel.style.maxHeight = "0px"
      panel.style.opacity = "0"
    })

    // Priority: current app context > localStorage
    let openKey = this.currentAppValue || null

    if (!openKey) {
      try {
        openKey = JSON.parse(localStorage.getItem(this.storageKey))
      } catch (_) { /* localStorage unavailable */ }
    }

    if (openKey) {
      const panel = this.findPanel(openKey)
      const section = this.findSection(openKey)
      if (panel && section) {
        section.classList.add("sidebar-app--open")
        panel.style.opacity = "1"
        panel.style.maxHeight = "none"
      }
    }

    // Re-enable transitions after paint so future interactions animate
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.panelTargets.forEach(panel => {
          panel.style.transition = ""
        })
      })
    })
  }
}
